import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RiCheckLine, RiFileCopyLine, RiPlugLine, RiRobot2Line } from '@remixicon/react'
import {
  Alert,
  Card,
  ConfirmDialog,
  Button,
  EmptyState,
  SectionSpinner,
  PageHeader,
} from '../../components/ui'
import { api, API_BASE_URL } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'
import { formatDateTime, formatRelativeTime } from '../../lib/dates'
import { cx, focusRing } from '../../lib/utils'

interface Connection {
  id: string
  client_id: string
  client_name: string
  scope: string
  created_at: string
  last_used_at: string | null
  expires_at: string
}

/**
 * Where an operator connects their own agent and sees what is connected.
 *
 * The URL is the whole setup: an MCP client discovers everything else from it,
 * registers itself, and sends the operator back here to approve. There is
 * nothing to paste and no token to keep.
 */
export default function AgentSettings() {
  const { t, i18n } = useTranslation()

  const [connections, setConnections] = useState<Connection[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const mcpUrl = `${API_BASE_URL.replace(/\/+$/, '')}/mcp`

  const load = useCallback(async () => {
    const response = await api.get<{ data: Connection[] }>('/api/oauth/connections')
    setConnections(response.data)
  }, [])

  useEffect(() => {
    load()
      .catch((err) => setError(toErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [load])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(mcpUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setError(t('agents.copyFailed'))
    }
  }

  const revoke = async (id: string) => {
    setError(null)
    try {
      await api.delete(`/api/oauth/connections/${id}`)
      await load()
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    }
  }

  if (loading) return <SectionSpinner />

  return (
    <>
      <PageHeader title={t('agents.title')} subtitle={t('agents.subtitle')} />

      {error && <Alert className="mb-6">{error}</Alert>}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
            {t('agents.connectTitle')}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('agents.connectDescription')}
          </p>

          {/* The address is the one thing on this page an operator has to
              carry elsewhere, so it is shown whole. It used to be truncated to
              one line, which hid the end of the host on a phone: a URL you
              cannot read is a URL you cannot check, and you would not know it
              was wrong until the client failed. It wraps instead, and says
              what it is. */}
          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-900">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {t('agents.urlLabel')}
            </p>
            <div className="mt-1.5 flex items-start gap-2">
              <code className="min-w-0 flex-1 break-all font-mono text-sm text-gray-900 dark:text-gray-100">
                {mcpUrl}
              </code>
              <button
                type="button"
                onClick={copy}
                aria-label={t('common.copy')}
                className={cx(
                  'shrink-0 rounded-md p-1.5 text-gray-400 transition hover:bg-gray-200 hover:text-gray-700 dark:hover:bg-gray-800',
                  focusRing
                )}
              >
                {copied ? (
                  <RiCheckLine className="size-4 text-emerald-600" aria-hidden="true" />
                ) : (
                  <RiFileCopyLine className="size-4" aria-hidden="true" />
                )}
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {t('agents.urlHint')}
            </p>
          </div>

          <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm text-gray-600 dark:text-gray-300">
            <li>{t('agents.step1')}</li>
            <li>{t('agents.step2')}</li>
            <li>{t('agents.step3')}</li>
          </ol>

          <div className="mt-4 rounded-md bg-primary-50 p-4 text-sm text-primary-900 dark:bg-primary-500/10 dark:text-primary-200">
            {t('agents.draftsOnly')}
          </div>
        </Card>

        <Card className="h-fit p-6">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
            {t('agents.connectedTitle')}
          </h2>

          {connections.length === 0 ? (
            <div className="mt-4">
              <EmptyState
                icon={<RiPlugLine className="size-6" aria-hidden="true" />}
                title={t('agents.noneTitle')}
                description={t('agents.noneDescription')}
              />
            </div>
          ) : (
            <ul className="mt-4 divide-y divide-gray-100 dark:divide-gray-800">
              {connections.map((connection) => (
                /* Stacked until there is room for a row. On a phone the name,
                   two facts about the connection and the Revoke button were
                   sharing 310px, which wrapped the middle one onto three lines
                   and squeezed it against the button. The button goes full
                   width there too: it is the only action, and a 83px target
                   floating beside wrapped text is easy to miss. */
                <li
                  key={connection.id}
                  className="flex flex-col gap-3 py-4 first:pt-0 sm:flex-row sm:items-center"
                >
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                    <RiRobot2Line className="size-4" aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-50">
                      {connection.client_name}
                    </p>
                    {/* One fact per line rather than joined by a separator:
                        the two answer different questions, and a middle dot
                        that lands at a wrap point reads as a typo. */}
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      {connection.last_used_at
                        ? t('agents.lastUsed', {
                            when: formatRelativeTime(connection.last_used_at, i18n.language),
                          })
                        : t('agents.neverUsed')}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('agents.connectedOn', {
                        date: formatDateTime(connection.created_at, i18n.language),
                      })}
                    </p>
                  </div>
                  <ConfirmDialog
                    title={t('agents.revokeTitle')}
                    description={t('agents.revokeDescription', { client: connection.client_name })}
                    confirmLabel={t('agents.revoke')}
                    onConfirm={() => revoke(connection.id)}
                    trigger={
                      <Button variant="secondary" className="w-full shrink-0 sm:w-auto">
                        {t('agents.revoke')}
                      </Button>
                    }
                  />
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  )
}
